import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { environment } from '../../environments/environment';
import { User, SimpleUser, NewUser } from './user.interfaces';
import { map, switchMap } from 'rxjs/operators';
import { forkJoin } from 'rxjs';

@Injectable({
	providedIn: 'root',
})
export class UserService {
	private userUrl = environment.apiUrl + 'Users'; // URL to web api

	constructor(private http: HttpClient) { }

	getSelf() {
		const url = `${environment.apiUrl}me`;
		return this.http.get<any>(url);
	}

	getAllUsers(siteId: string) {
		const url = `${this.userUrl}/FromSite/${siteId}`;
		return this.http.get(url);
	}

	getUser(userId: string) {
		const url = `${this.userUrl}/${userId}`;
		return this.http.get<User>(url);
	}

	addUser(user: NewUser) {
		const url = `${this.userUrl}`;
		return this.http.post(url, user);
	}

	setDefaultSiteForUser(siteId: string) {
		const url = `${this.userUrl}/Profile/SetDefaultSiteId/${siteId}`;
		return this.http.put(url, null);
	}

	updateUser(userId: string, user: NewUser) {
		const url = `${this.userUrl}/${userId}`;
		return this.http.put(url, user);
	}

	updateUsers(users: NewUser[]) {
		// loops through users to be updates and calls the api endpoint for each user
		// forkJoin waits for all observables to return
		return forkJoin(users.map(user => {
				const url = `${this.userUrl}/${user.id}`;
				return this.http.put(url, user)
			})
		)
	}

	deactivateUser(userId: string, siteId: string) {
		const url = `${this.userUrl}/${userId}/FromSite/${siteId}`;
		return this.http.delete(url);
	}

	userIsAdmin(user: User, siteId: string) {
		return user.sites.some(site => site.siteId === siteId && site.userIsAdmin === true);
	}

	userIsContributor(user: User, siteId: string) {
		return user.sites.some(site => 
			site.siteId === siteId &&
			site.userIsContributor === true &&
			site.userIsAdmin === false
		);
	}

	userIsReader(user: User, siteId: string) {
		return user.sites.some(site => 
			site.siteId === siteId &&
			site.userIsContributor === false &&
			site.userIsAdmin === false
		);
	}

	userCanWrite(user: User, siteId: string) {
		return this.userIsAdmin(user, siteId) || this.userIsContributor(user, siteId);
	}

	getRoleString(user: User, siteId: string) {
		if(this.userIsAdmin(user, siteId)) return "Admin";
		if(this.userIsContributor(user, siteId)) return "Contributor";
		if(this.userIsReader(user, siteId)) return "Reader";
	}

	buildUserSite(siteId: string, roleString: String) {
		let site = { siteId: siteId };
		if (roleString === "Admin") {
			return { ...site, userIsAdmin: true, userIsContributor: false };
		} else if (roleString === "Contributor") {
			return { ...site, userIsAdmin: false, userIsContributor: true };
		} else if (roleString === "Reader") {
			return { ...site, userIsAdmin: false, userIsContributor: false };
		}
	}

	getUsersForSiteByFirstName(searchTerm: string) {
		return this.getUsersForSite('ByFirstName', searchTerm);
	}

	getUsersForSiteByLastName(searchTerm: string) {
		const url = `${this.userUrl}/ByLastName/${searchTerm}`;
		return this.http.get<User[]>(url).pipe(
			map(response => {
				return response.map(user => {
					const simpleUser = new SimpleUser();
					simpleUser.id = user.id;
					simpleUser.name = `${user.lastName}, ${user.firstName}`;
					simpleUser.email = user.email;
					simpleUser.firstName = user.firstName;
					simpleUser.lastName = user.lastName;
					return simpleUser;
				});
			})
		);
	}

	getUsersForSiteByEmail(searchTerm: string) {
		return this.getUsersForSite('ByEmail', searchTerm);
	}

	acceptEula() {
		const url = `${this.userUrl}/Profile/AcceptEULA/`;
		return this.http.put(url, null);
	}

	private getUsersForSite(searchProperty: string, searchTerm: string) {
		const url = `${this.userUrl}/${searchProperty}/${searchTerm}`;
		return this.http.get<User[]>(url).pipe(
			map(response => {
				return response.map(user => {
					const simpleUser = new SimpleUser();
					simpleUser.id = user.id;
					simpleUser.name = `${user.firstName} ${user.lastName}`;
					simpleUser.email = user.email;
					simpleUser.firstName = user.firstName;
					simpleUser.lastName = user.lastName;
					return simpleUser;
				});
			})
		);
	}
}
